'use client';
import Link from "next/link";
import { useTranslations, useLocale } from "next-intl";
import { useEffect, useRef } from "react";

export default function NotFound() {
  const t = useTranslations("NotFound");
  const locale = useLocale();

  const accentRef = useRef<HTMLDivElement>(null);
  const codeRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const btnRef = useRef<HTMLAnchorElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let ctx: any;
    import("gsap").then(({ gsap }) => {
      if (cancelled) return;
      ctx = gsap.context(() => {
        const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
        tl
          .from(accentRef.current, {
            scaleX: 0,
            transformOrigin: "left center",
            duration: 0.7,
            ease: "power2.inOut",
          })
          .from(codeRef.current, { y: 60, opacity: 0, duration: 0.9 }, "-=0.3")
          .from(titleRef.current, { y: 30, opacity: 0, duration: 0.7 }, "-=0.5")
          .from(subtitleRef.current, { y: 20, opacity: 0, duration: 0.6 }, "-=0.4")
          .from(lineRef.current, { scaleX: 0, transformOrigin: "left center", duration: 0.5 }, "-=0.3")
          .from(btnRef.current, { y: 16, opacity: 0, duration: 0.5 }, "-=0.2");
      });
    });
    return () => { cancelled = true; ctx?.revert(); };
  }, []);

  return (
    <section className="relative w-full min-h-[calc(100vh-80px)] flex flex-col justify-center px-6 md:px-20 overflow-hidden">
      <div className="max-w-3xl">
        <div ref={accentRef} className="w-16 h-1.5 bg-[#b91c1c] mb-8" />

        <div ref={codeRef} className="overflow-hidden">
          <span className="block text-[8rem] md:text-[14rem] font-bold leading-none tracking-tighter text-[#b91c1c] select-none">
            {t("code")}
          </span>
        </div>

        <h1
          ref={titleRef}
          className="text-2xl md:text-5xl font-semibold uppercase tracking-wide mt-2"
        >
          {t("title")}
        </h1>

        <p
          ref={subtitleRef}
          className="mt-4 text-sm md:text-base text-gray-500 max-w-md leading-relaxed"
        >
          {t("subtitle")}
        </p>

        <div ref={lineRef} className="w-full max-w-md h-px bg-gray-200 mt-8 mb-8" />

        <Link
          ref={btnRef}
          href={`/${locale}`}
          className="inline-flex items-center gap-3 group"
        >
          <span className="w-8 h-px bg-[#b91c1c] transition-all duration-300 group-hover:w-14" />
          <span className="text-sm md:text-base font-medium uppercase tracking-widest hover:text-[#b91c1c] transition-colors duration-300">
            {t("go_home")}
          </span>
        </Link>
      </div>

      <div
        className="absolute right-0 bottom-0 text-[20rem] md:text-[32rem] font-bold leading-none tracking-tighter select-none pointer-events-none"
        style={{ color: "rgba(0,0,0,0.03)" }}
        aria-hidden
      >
        404
      </div>
    </section>
  );
}